import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AcceptanceSession } from "@/components/AcceptanceSession";

/**
 * CTO Directive #129 §P0.4 — Pilot Dashboard.
 *
 * The honest operational mirror of the Production Candidate pilot:
 * crash / latency / failed command / retry / cast failure / voice
 * failure / microphone failure / backend timeout — nothing hidden,
 * nothing invented.
 */
const KIND_ORDER = [
  "crash", "backend_timeout", "voice_failure", "microphone_failure",
  "cast_failure", "failed_command", "retry", "latency",
];

export default function PilotDashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/pilot/dashboard");
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e.message || "yüklenemedi");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) {
    return (
      <div className="p-8 text-sm text-slate-400" data-testid="pilot-dashboard-loading">
        {error ? `Pilot verisi yüklenemedi: ${error}` : "Pilot verisi yükleniyor…"}
      </div>
    );
  }

  const labels = data.kind_labels_tr || {};

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6" data-testid="pilot-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100">Pilot Dashboard</h1>
        <p className="mt-1 text-sm text-slate-400" data-testid="pilot-summary">
          {data.summary_tr}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {KIND_ORDER.map((k) => (
          <div
            key={k}
            className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4"
            data-testid={`pilot-count-${k}`}
          >
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
              {labels[k] || k}
            </p>
            <p className="mt-1 text-2xl font-semibold text-slate-100">
              {data.counts?.[k] ?? 0}
            </p>
            <p className="text-[11px] text-slate-500">
              son 24s: {data.counts_24h?.[k] ?? 0}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4" data-testid="pilot-latency">
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">
          Konuşma gecikmesi (son 24 saat)
        </p>
        <p className="mt-1 text-sm text-slate-200">
          Ortalama: {data.latency?.avg_ms != null ? `${data.latency.avg_ms} ms` : "örnek yok"}
          {" · "}En yüksek: {data.latency?.max_ms != null ? `${data.latency.max_ms} ms` : "—"}
          {" · "}Örnek: {data.latency?.samples_24h ?? 0}
        </p>
      </div>

      <AcceptanceSession />

      <div>
        <h2 className="text-base font-medium text-slate-200">Son olaylar</h2>
        <div className="mt-2 space-y-1" data-testid="pilot-recent-list">
          {(data.recent || []).length === 0 && (
            <p className="text-sm text-slate-500">Henüz kayıtlı olay yok.</p>
          )}
          {(data.recent || []).map((ev) => (
            <div
              key={ev.id}
              className="flex items-baseline gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm"
            >
              <span className="shrink-0 text-[11px] uppercase tracking-wide text-slate-500">
                {ev.kind_label_tr || ev.kind}
              </span>
              <span className="truncate text-slate-300">{ev.note || "—"}</span>
              {ev.latency_ms != null && (
                <span className="text-slate-500">{Math.round(ev.latency_ms)} ms</span>
              )}
              <span className="ml-auto shrink-0 text-[11px] text-slate-600">
                {String(ev.created_at || "").slice(0, 19).replace("T", " ")}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
